import { FC, ReactNode, useContext, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  createStyles,
  Grid,
  Hidden,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import {
  AttachMoney as AttachMoneyIcon,
  BusinessCenter as BusinessCenterIcon,
  Equalizer as EqualizerIcon,
  OpenInNew as OpenInNewIcon,
} from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import Fuse from "fuse.js";
import { includes, isEqual } from "lodash";
import { Link } from "react-router-dom";

import {
  Button,
  CheckboxField,
  FilterListButton,
  Modal,
  OverviewCard,
  ResetButton,
  SearchField,
} from "components";
import {
  INVESTOR_VERIFICATION_URL,
  TROLLEY_PAYMENT_PORTAL_URL,
} from "constants/index";
import { BaseContainer } from "containers";
import { AppContext } from "contexts";
import { defaultErrorMessage } from "lib/axios/constants";
import * as routes from "routes";
import { useManyAssetAdministrations } from "services/assetAdministrations";
import { longDebounceTime } from "services/constants";
import {
  ALL_10_YEAR_TERMS,
  ALL_30_YEAR_TERMS,
  ALL_FIXED_RETURN_TERMS,
  ALL_LIFE_OF_RIGHTS_TERMS,
} from "services/listings/constants";
import { useManyOffers } from "services/offers";
import theme from "theme";

import AssetAdministrationModal from "./components/AssetAdministrationModal";
import AssetCard from "./components/AssetCard";
import AssetsTable, { Column, defaultSort } from "./components/AssetsTable";
import DealCard from "./components/DealCard";
import {
  BuyerDashboardItem,
  getBuyerDashboardItems,
  getStats,
} from "./assetAdministrationsPostProcessor";
import { RELATED_ADJUSTMENTS_ISSUED_COPY } from "./constants";
import useFilteredBuyerDashboardItems, { FilterOption } from "./hooks";

const useStyles = makeStyles(() =>
  createStyles({
    noAssetsContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    noAssetsText: {
      fontSize: "1.4rem",
      marginBottom: theme.spacing(2),
    },
    sectionTitle: {
      fontSize: "1.8rem",
      fontWeight: theme.typography.fontWeightRegular,
    },
    cardsContainer: {
      maxWidth: "145px",
    },
    modalTitle: {
      fontSize: "2rem",
      fontWeight: theme.typography.fontWeightBold,
    },
    filterLabelModal: {
      fontSize: "1.125rem",
      fontWeight: theme.typography.fontWeightBold,
    },
    filterLabel: {
      fontWeight: theme.typography.fontWeightMedium,
    },
    filterOption: {
      display: "flex",
      alignItems: "center",
      color: theme.custom.palette.gray.dark,
      fontSize: "1.1rem",
      whiteSpace: "nowrap",
    },
    filterButton: {
      fontSize: "1rem",
      fontWeight: theme.typography.fontWeightRegular,
      padding: theme.spacing(0, 1),
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    filterButtonActive: {
      color: theme.custom.palette.purple.main,
    },
    filterButtonInactive: {
      color: theme.custom.palette.gray.dark,
    },
    alertInfo: {
      display: "flex",
      alignItems: "center",
      justifyContent: "left",
      textAlign: "left",
      marginBottom: theme.spacing(1),
    },
    link: {
      color: theme.custom.palette.blue.main,
      fontSize: "inherit",
      "&:hover": {
        color: theme.custom.palette.blue.main,
        textDecoration: "underline",
      },
    },
  }),
);

const typeFilterOptions: FilterOption[] = [
  {
    label: "Held",
    key: "isHeld",
    value: [true],
  },
  {
    label: "Sold",
    key: "isHeld",
    value: [false],
  },
];

const termFilterOptions: FilterOption[] = [
  {
    label: "Life of Rights",
    key: "purchasedTerm",
    value: ALL_LIFE_OF_RIGHTS_TERMS,
  },
  {
    label: "30-Year",
    key: "purchasedTerm",
    value: ALL_30_YEAR_TERMS,
  },
  {
    label: "10-Year",
    key: "purchasedTerm",
    value: ALL_10_YEAR_TERMS,
  },
  {
    label: "Fixed Return",
    key: "purchasedTerm",
    value: ALL_FIXED_RETURN_TERMS,
  },
];

const filters = [
  { label: "Type", options: typeFilterOptions },
  { label: "Term", options: termFilterOptions },
];

type ResponsiveHiddenProps = {
  children: ReactNode;
  xsDown?: boolean;
  mdDown?: boolean;
  mdUp?: boolean;
  smDown?: boolean;
  lgUp?: boolean;
};

// Fix this when we upgrade to MUI v5 in RXT-2628
const ResponsiveHidden: FC<ResponsiveHiddenProps> = props => {
  return <Hidden implementation="js" {...props} />;
};

const BuyerDashboard: FC = () => {
  const classes = useStyles();
  const isXsDown = useMediaQuery(theme.breakpoints.down("xs"));
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const { user } = useContext(AppContext);
  const hasBuyListingsPermission =
    user && includes(user.permissionList, "buy_listings");

  const assetAdministrationsQueries = useManyAssetAdministrations();
  const pendingDealQueries = useManyOffers(
    `?filter{state.startswith}=awaiting&filter{listing_state}=pending&filter{buyer}=${user.userId}`,
  );

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [
    selectedBuyerDashboardItemResaleInfoId,
    setSelectedBuyerDashboardItemResaleInfoId,
  ] = useState<number | null>(null);

  const buyerDashboardItems = useMemo(() => {
    return getBuyerDashboardItems(assetAdministrationsQueries.data ?? []);
  }, [assetAdministrationsQueries.data]);

  const fuse = useMemo(
    () =>
      new Fuse(buyerDashboardItems, {
        useExtendedSearch: true,
        keys: ["dealTitle", "previousHubspotDealId"],
        // https://www.fusejs.io/api/options.html#distance
        distance: 167, // keeping default "threshold" of 0.6, this should match up to 100 characters (167/100 = 0.6)
      }),
    [buyerDashboardItems],
  );
  const [searchQuery, setSearchQuery] = useState<string>("");
  const searchQueryFilteredBuyerDashboardItems = useMemo(
    () =>
      !!searchQuery
        ? fuse
            .search(
              // https://www.fusejs.io/api/query.html#use-with-extended-searching
              {
                $or: [
                  {
                    dealTitle: searchQuery,
                  },
                  {
                    previousHubspotDealId: `'${searchQuery}`, // must exact match
                  },
                ],
              },
            )
            .map(result => result.item)
        : buyerDashboardItems,
    [buyerDashboardItems, fuse, searchQuery],
  );

  const [
    filteredBuyerDashboardItems,
    activeFilterOptions,
    setActiveFilterOptions,
    handleFilterChange,
  ] = useFilteredBuyerDashboardItems(searchQueryFilteredBuyerDashboardItems);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const stats = getStats(filteredBuyerDashboardItems);
  const overviewCards = [
    {
      icon: <BusinessCenterIcon />,
      label: "Assets (Held/Sold)",
      value: `${stats.heldCount}/${stats.soldCount}`,
      color: theme.custom.palette.purple.main,
    },
    {
      icon: <AttachMoneyIcon />,
      label: "Royalties Received",
      value: stats.totalRoyaltiesReceived,
      color: theme.custom.palette.green.main,
    },

    {
      icon: <EqualizerIcon />,
      label: "Average Yield (Held)",
      value: stats.averageYield,
      color: theme.custom.palette.purple.main,
    },
    {
      icon: <AttachMoneyIcon />,
      label: "Sale Proceeds",
      value: stats.totalSaleProceeds,
      color: theme.custom.palette.green.main,
    },
    {
      icon: <EqualizerIcon />,
      label: "Average ROI (Sold)",
      value: stats.averageROI,
      color: theme.custom.palette.purple.main,
    },
    {
      icon: <AttachMoneyIcon />,
      label: "Return (Sold)",
      value: stats.totalReturn,
      color: theme.custom.palette.green.main,
    },
  ];

  const [sortColumn, setSortColumn] = useState<Column>(defaultSort.column);
  const [isSortingAsc, setIsSortingAsc] = useState<boolean>(
    defaultSort.isSortingAsc,
  );
  const handleReset = () => {
    setSearchQuery("");
    setActiveFilterOptions([]);
    setSortColumn(defaultSort.column);
    setIsSortingAsc(true);
  };
  const isResetButtonDisabled =
    !searchQuery &&
    activeFilterOptions.length === 0 &&
    isEqual({ column: sortColumn, isSortingAsc }, defaultSort);

  // smDown (cards):
  //   446px is the height of a card, has 24px margin bottom
  //   using single card height to prevent long scroll down on mobile when there are no results
  // !smDown (table)
  //   108px is approximately the height of a single row in the table with 2 lines of title + gap row
  //   60px is the height of the table header
  //   use a max of 5 rows visible
  const minHeightAssetArea = isSmDown
    ? 446 + 24
    : 108 * Math.min(buyerDashboardItems.length, 5) + 61;

  const handleDetailsClick = (buyerDashboardItem: BuyerDashboardItem) => {
    setSelectedBuyerDashboardItemResaleInfoId(buyerDashboardItem.resaleInfoId);
    setIsDetailsModalOpen(true);
  };
  const hasPendingDeals = !!pendingDealQueries.data?.length;

  const hasAssetAdministrations = !!assetAdministrationsQueries.data?.length;

  const hasNoAssets = !hasAssetAdministrations && !hasPendingDeals;
  const isBuyerDashboardLoading =
    pendingDealQueries.isLoading || assetAdministrationsQueries.isLoading;

  return (
    <BaseContainer>
      {isFilterModalOpen && (
        <Modal open onClose={() => setIsFilterModalOpen(false)}>
          <Box marginTop={-6}>
            <Box mb={3}>
              <Typography
                variant="h2"
                gutterBottom
                className={classes.modalTitle}
              >
                Filters
              </Typography>
            </Box>
            {filters.map(filter => (
              <Box
                key={filter.label}
                display="flex"
                flexDirection="column"
                mb={2}
              >
                <Typography gutterBottom className={classes.filterLabelModal}>
                  {filter.label}
                </Typography>
                {filter.options.map(option => (
                  <CheckboxField
                    dense
                    key={option.label}
                    checked={activeFilterOptions.includes(option)}
                    onChange={() => handleFilterChange(option)}
                    label={option.label}
                    checkboxColor={theme.custom.palette.purple.main}
                  />
                ))}
              </Box>
            ))}
          </Box>
          <Box
            width="100%"
            display="flex"
            justifyContent="center"
            marginBottom={3}
          >
            <ResetButton
              disabled={isResetButtonDisabled}
              handleReset={handleReset}
            />
          </Box>
        </Modal>
      )}
      <Box
        mt={5}
        mb={5}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexDirection={isXsDown ? "column" : "row"}
      >
        <Typography variant="h1" gutterBottom={isXsDown}>
          Buyer Dashboard
        </Typography>
        {!isBuyerDashboardLoading && !hasNoAssets && (
          <Button
            customColor="green"
            variant="contained"
            component="a"
            href={TROLLEY_PAYMENT_PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Payment Portal&nbsp;&nbsp;
            <OpenInNewIcon />
          </Button>
        )}
      </Box>
      {!isBuyerDashboardLoading && hasNoAssets ? (
        <Box mt={5} className={classes.noAssetsContainer}>
          <Typography className={classes.noAssetsText}>
            You do not own any assets.{" "}
            {hasBuyListingsPermission
              ? "Make your first purchase."
              : "Get your account verified to start placing bids."}
          </Typography>
          {hasBuyListingsPermission ? (
            <Button
              component={Link}
              to={routes.overview()}
              customColor="green"
              variant="contained"
            >
              Go to The Exchange
            </Button>
          ) : (
            <Button
              customColor="green"
              variant="contained"
              component="a"
              href={INVESTOR_VERIFICATION_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Get Verified
            </Button>
          )}
        </Box>
      ) : (
        <>
          {pendingDealQueries.isError && <div>{defaultErrorMessage}</div>}
          {hasPendingDeals && (
            <Box>
              <Typography
                variant="h2"
                className={classes.sectionTitle}
                gutterBottom
              >
                Pending Purchases
              </Typography>
              <Box my={1}>
                <Grid container spacing={1}>
                  {pendingDealQueries.data.map(offer => {
                    return (
                      <Grid item key={offer.id} xs={12} md={6}>
                        <DealCard offer={offer} />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>
          )}
          {isBuyerDashboardLoading && (
            <Box pt={2}>
              <CircularProgress />
            </Box>
          )}
          {assetAdministrationsQueries.isError && (
            <div>{defaultErrorMessage}</div>
          )}
          {hasAssetAdministrations && (
            <>
              <Typography
                variant="h2"
                className={classes.sectionTitle}
                gutterBottom
              >
                Assets
              </Typography>

              <Box mb={2}>
                <Alert severity="info" className={classes.alertInfo}>
                  <Typography>{RELATED_ADJUSTMENTS_ISSUED_COPY}</Typography>
                </Alert>
              </Box>
              <Box mb={3} mt={1}>
                <Grid container spacing={1}>
                  {overviewCards.map((card, index) => {
                    return (
                      <Grid item xs={6} sm={4} md={2} key={index}>
                        <OverviewCard {...card} />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
              <Box
                mb={3}
                display="flex"
                justifyContent="space-between"
                flexDirection={isSmDown ? "column" : "row"}
              >
                <Box maxWidth={isSmDown ? "100%" : "50%"} width="100%">
                  <SearchField
                    activeQuery={searchQuery}
                    debounceTime={longDebounceTime}
                    fieldId="buyer-dashboard-search"
                    onChangeCommitted={setSearchQuery}
                    placeholder="Search by Title or Deal ID"
                  />
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  mt={isSmDown ? 3 : 0}
                >
                  <ResponsiveHidden mdUp>
                    <FilterListButton
                      label="Filter"
                      onClick={() => setIsFilterModalOpen(true)}
                    />
                  </ResponsiveHidden>
                  <ResetButton
                    disabled={isResetButtonDisabled}
                    handleReset={handleReset}
                  />
                </Box>
              </Box>
              <ResponsiveHidden smDown>
                <Box
                  mb={3}
                  display="flex"
                  justifyContent="space-between"
                  flexDirection={isSmDown ? "column" : "row"}
                >
                  {filters.map((filter, i) => (
                    <Box
                      key={filter.label}
                      width="100%"
                      display="flex"
                      alignItems="center"
                      justifyContent={
                        i === filters.length - 1 ? "flex-end" : "flex-start"
                      }
                    >
                      <Typography className={classes.filterLabel}>
                        {filter.label}:
                      </Typography>
                      {filter.options.map((option, i) => {
                        return (
                          <Box
                            key={option.label}
                            className={classes.filterOption}
                          >
                            <Button
                              className={`${classes.filterButton} ${
                                activeFilterOptions.includes(option)
                                  ? classes.filterButtonActive
                                  : classes.filterButtonInactive
                              }`}
                              disableRipple
                              onClick={() => handleFilterChange(option)}
                            >
                              {option.label}
                            </Button>
                            {i < filter.options.length - 1 && <> / </>}
                          </Box>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              </ResponsiveHidden>
              <Box minHeight={minHeightAssetArea}>
                {!!filteredBuyerDashboardItems.length ? (
                  <>
                    {isSmDown ? (
                      <Box mb={3}>
                        {filteredBuyerDashboardItems.map(buyerDashboardItem => {
                          return (
                            <Box mb={3} key={buyerDashboardItem.resaleInfoId}>
                              <AssetCard
                                buyerDashboardItem={buyerDashboardItem}
                                onDetailsClick={handleDetailsClick}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box mb={3}>
                        <AssetsTable
                          buyerDashboardItems={filteredBuyerDashboardItems}
                          sortColumn={sortColumn}
                          setSortColumn={setSortColumn}
                          isSortingAsc={isSortingAsc}
                          setIsSortingAsc={setIsSortingAsc}
                          onDetailsClick={handleDetailsClick}
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography variant="h6">
                    No Assets match your current search
                  </Typography>
                )}
              </Box>
            </>
          )}
        </>
      )}
      {isDetailsModalOpen && (
        <AssetAdministrationModal
          buyerDashboardItem={buyerDashboardItems.find(
            item =>
              item.resaleInfoId === selectedBuyerDashboardItemResaleInfoId,
          )}
          onClose={() => setIsDetailsModalOpen(false)}
        />
      )}
    </BaseContainer>
  );
};

export default BuyerDashboard;
